"use client";
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  styled,
  TextField,
} from "@mui/material";
import React, { ReactElement, useState } from "react";
import {
  faCircleCheck,
  faCircleExclamation,
  faEye,
  faEyeSlash,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { faCircleXmark } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { VeBox, VeTypography } from "../../mui";
import InfoIcon from "./InfoIcon";
import { VeTooltip } from "..";

interface InputProps {
  label?: string | JSX.Element;
  error?: boolean;
  search?: boolean;
  errorText?: string;
  style?: React.CSSProperties;
  register?: any;
  options?: Object;
  name: string;
  helperText?: string;
  placeHolder?: string;
  value?: string | number;
  required?: boolean;
  shrink?: boolean;
  type?:
    | "text"
    | "number"
    | "email"
    | "password"
    | "file"
    | "date"
    | "textarea";
  multiLineRows?: number;
  disabled?: boolean;
  endAdornment?: ReactElement;
  startAdornment?: ReactElement;
  checkValidate?: boolean;
  autoFocus?: boolean;
  onBlur?: any;
  onChange?: any;
  defaultValue?: any;
  onKeyDown?: any;
  onKeyUp?: any;
  onFocus?: any;
  color?: string;
  requiredLabel?: boolean;
  inputHeight?: string;
  readOnly?: boolean;
  infoAtEnd?: boolean;
  tooltipTitle?: string;
  inputRef?: any;
  clearInput?: boolean;
  setValue?: any;
  dynamicLabel?: string;
  /** Comma-separated unit options string OR array of unit strings. When provided, a unit dropdown is rendered inside the input. */
  unitOptions?: string | string[];
  /** Currently selected unit value */
  selectedUnit?: string;
  /** Callback when unit selection changes */
  onUnitChange?: (unit: string) => void;
}

const StyledTextField = styled(TextField)(() => ({
  "& .MuiFormHelperText-root .Mui-error": {
    color: "#DE1135",
    fontFamily: "var(--font-inter)",
    fontSize: "10px",
    lineHeight: "14px",
    marginLeft: "4px !important",
  },

  "& .MuiFormHelperText-root": {
    color: "#646E83",
    fontFamily: "var(--font-inter)",
    fontSize: "10px",
    lineHeight: "14px",
    marginLeft: "4px !important",
  },
  "& .MuiInputLabel-root.MuiInputLabel-asterisk": {
    color: "#DE1135", // Change color of the asterisk to red
  },
}));

const CloseIcon = ({ onClick }: any) => {
  return (
    <svg
      style={{ cursor: "pointer" }}
      onClick={onClick}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.4406 5.73483C15.6575 5.51787 15.6575 5.16611 15.4406 4.94916L15.0513 4.55983C14.8343 4.34287 14.4825 4.34287 14.2656 4.55983L10.0001 8.82533L5.73459 4.55983C5.51763 4.34287 5.16587 4.34287 4.94891 4.55983L4.55959 4.94916C4.34263 5.16611 4.34263 5.51787 4.55959 5.73483L8.82508 10.0003L4.55959 14.2658C4.34263 14.4828 4.34263 14.8345 4.55959 15.0515L4.94891 15.4408C5.16587 15.6578 5.51763 15.6578 5.73459 15.4408L10.0001 11.1753L14.2656 15.4408C14.4825 15.6578 14.8343 15.6578 15.0513 15.4408L15.4406 15.0515C15.6575 14.8345 15.6575 14.4828 15.4406 14.2658L11.1751 10.0003L15.4406 5.73483Z"
        fill="#5F6368"
      />
    </svg>
  );
};

export function VeInput({
  label,
  error,
  search,
  type = "text",
  errorText,
  style,
  name,
  register,
  defaultValue,
  value,
  helperText,
  placeHolder,
  disabled,
  required,
  multiLineRows = undefined,
  endAdornment,
  startAdornment,
  checkValidate,
  onBlur,
  autoFocus,
  onChange,
  shrink,
  onKeyDown,
  onKeyUp,
  onFocus,
  color = "#3D465A",
  requiredLabel,
  inputHeight = "12px",
  readOnly = false,
  infoAtEnd = false,
  tooltipTitle,
  inputRef,
  clearInput = true,
  setValue,
  dynamicLabel,
  unitOptions,
  selectedUnit,
  onUnitChange,
}: InputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const [inputHovered, setInputHovered] = useState(false);
  const [inputActive, setInputActive] = useState(false);

  const internalRef = React.useRef<HTMLInputElement | null>(null);
  const effectiveRef = inputRef || internalRef;

  const hasValue = !!effectiveRef.current?.value?.trim();

  // Parse unit options: accept comma-separated string or array
  const parsedUnits: string[] = React.useMemo(() => {
    if (!unitOptions) return [];
    if (Array.isArray(unitOptions)) return unitOptions.filter(Boolean);
    return String(unitOptions)
      .split(",")
      .map((u) => u.trim())
      .filter(Boolean);
  }, [unitOptions]);
  const showUnitDropdown = parsedUnits.length > 0;

  // Need this if it doesnt work in certain browsers

  // const handleClearInput = (e: React.MouseEvent) => {
  //   // if (effectiveRef.current) {
  //   // Clear the input value using setValue

  //   e.preventDefault();
  //   e.stopPropagation();

  //   if (!name || !setValue) return;

  //   setValue(name, "", {
  //     shouldValidate: true,
  //     shouldDirty: true,
  //     shouldTouch: true,
  //   }); // Clear value in React Hook Form state
  //   effectiveRef.current.value = ""; // Clear the input field visually
  //   // }
  // };

  const handleClearInput = () => {
    // This is basically mimicking whatever happens under the hood of RHF when we reset / setValue.
    if (!effectiveRef.current) return;

    // Clear value via native setter
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value",
    )?.set;

    nativeInputValueSetter?.call(effectiveRef.current, "");

    // Fire input & change events to notify RHF
    const eventOptions = { bubbles: true };

    effectiveRef.current.dispatchEvent(new Event("input", eventOptions));
    effectiveRef.current.dispatchEvent(new Event("change", eventOptions));
  };

  const textFieldSx = {
    "& .MuiInputBase-input": {
      color: `${color} !important`,
      weight: 400,
    },
    "& .MuiInputLabel-root": {
      color: "#3D465A !important",
    },
    "& .MuiInputLabel-root.Mui-disabled": {
      color: "#8F9295 !important",
    },
    "& .MuiOutlinedInput-input": {
      color,
      height: inputHeight,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: showUnitDropdown
        ? "4px 0 0 4px !important"
        : "4px !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input": {
      color: "#1D2330 !important",
    },
    "& .MuiInputLabel-asterisk.Mui-required": {
      color: "#DE1135",
    },
    ...(showUnitDropdown && {
      "& .MuiOutlinedInput-notchedOutline": {
        borderRight: "none",
      },
    }),
  };

  const textField = (
    <StyledTextField
      sx={textFieldSx}
      onMouseEnter={() => setInputHovered(true)}
      onMouseLeave={() => setInputHovered(false)}
      onKeyDownCapture={(evt) =>
        type === "number" &&
        ["e", "E", "+", "-"].includes(evt.key) &&
        evt.preventDefault()
      }
      // onFocus={onFocus}
      // onBlur={onBlur}
      onFocus={(e) => {
        setInputActive(true);
        onFocus?.(e);
      }}
      inputRef={effectiveRef}
      // ref={ref}
      InputLabelProps={shrink && { shrink: true }}
      fullWidth
      onChange={onChange}
      disabled={disabled}
      type={showPassword && type === "password" ? "text" : type}
      {...(register && register(name))}
      error={error}
      placeholder={placeHolder}
      name={name}
      autoFocus={autoFocus}
      value={value}
      defaultValue={defaultValue}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      label={
        required || requiredLabel ? (
          <VeBox display={"flex"}>
            {inputActive ? (dynamicLabel ? dynamicLabel : label) : label}
            <VeBox marginLeft={"2px"} color={"red"}>
              *
            </VeBox>
          </VeBox>
        ) : inputActive ? (
          dynamicLabel ? (
            dynamicLabel
          ) : (
            label
          )
        ) : (
          label
        )
      }
      multiline={!!multiLineRows}
      rows={multiLineRows || undefined}
      helperText={(error && errorText) || helperText}
      InputProps={{
        inputProps: {
          readOnly,
        },
        onBlur: (e) => {
          setInputActive(false);
          setInputHovered(false);
          onBlur?.(e);
        },
        endAdornment:
          !disabled &&
          type !== "password" &&
          !infoAtEnd &&
          clearInput &&
          !multiLineRows &&
          (inputHovered || inputActive) &&
          hasValue ? (
            <InputAdornment
              onMouseDown={(e) => e.preventDefault()}
              sx={{ cursor: "pointer" }}
              position="end"
            >
              {/* <FontAwesomeIcon
                  onClick={handleClearInput}
                  icon={faCircleXmark}
                  color="#1D2330"
                /> */}
              <CloseIcon onClick={handleClearInput} />
            </InputAdornment>
          ) : infoAtEnd ? (
            <InputAdornment position="end">
              <VeTooltip
                placement="bottom"
                arrow
                title={
                  tooltipTitle!?.length > 0 ? (
                    <VeTypography
                      sx={{
                        padding: "4px 8px",
                        display: "flex",
                        alignItems: "center",
                        maxWidth: "300px",
                      }}
                      variant="label4MainWhite"
                    >
                      {tooltipTitle}
                    </VeTypography>
                  ) : (
                    ""
                  )
                }
              >
                <VeBox
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <InfoIcon disabled={disabled} />
                </VeBox>
              </VeTooltip>
            </InputAdornment>
          ) : endAdornment ? (
            <VeBox>{endAdornment}</VeBox>
          ) : type === "password" ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                edge="end"
                sx={{ height: "31px" }}
              >
                {showPassword ? (
                  <FontAwesomeIcon
                    width={"15px"}
                    height={"15px"}
                    icon={faEye}
                  />
                ) : (
                  <FontAwesomeIcon
                    width={"15px"}
                    height={"15px"}
                    icon={faEyeSlash}
                  />
                )}
              </IconButton>
            </InputAdornment>
          ) : error ? (
            <>
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  edge="end"
                >
                  <FontAwesomeIcon
                    color="#DE1135"
                    width={"15px"}
                    height={"15px"}
                    icon={faCircleExclamation}
                  />
                </IconButton>
              </InputAdornment>
            </>
          ) : (
            checkValidate && (
              <>
                <InputAdornment position="end">
                  <IconButton aria-label="check" edge="end">
                    <FontAwesomeIcon
                      color="#DE1135"
                      width={"15px"}
                      height={"15px"}
                      icon={faCircleCheck}
                      style={{ color: "#0e8345" }}
                    />
                  </IconButton>
                </InputAdornment>
              </>
            )
          ),
        startAdornment: startAdornment
          ? startAdornment
          : search && (
              <InputAdornment position="start">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </InputAdornment>
            ),
      }}
    />
  );

  return (
    <Box width={"100%"} sx={{ ...style }}>
      {showUnitDropdown ? (
        <Box display="flex" alignItems="stretch" width="100%">
          <Box flex={1}>{textField}</Box>
          <Select
            size="small"
            value={selectedUnit ?? parsedUnits[0] ?? ""}
            onChange={(e) => onUnitChange?.(e.target.value as string)}
            sx={{
              minWidth: 80,
              borderRadius: "0 4px 4px 0",
              border: "1px solid #E8E8E8",
              // borderLeft: "none",
              "& fieldset": { border: "none" },
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
              },
            }}
          >
            {parsedUnits.map((unit) => (
              <MenuItem key={unit} value={unit}>
                {unit}
              </MenuItem>
            ))}
          </Select>
        </Box>
      ) : (
        textField
      )}
    </Box>
  );
}
